export type PermissionId = string | number

export type Permission = {
  id_feature: PermissionId
  title: string
  name: string
  route?: string
  type?: string
  is_active?: boolean | number | string | null
}

export type PermissionStatusFilter = 'active' | 'inactive' | 'all'

export function isPermissionActive(permission: Pick<Permission, 'is_active'>) {
  return permission.is_active == null || permission.is_active === true ||
    permission.is_active === 1 || permission.is_active === '1'
}

export function matchesPermissionStatus(permission: Permission, filter: PermissionStatusFilter) {
  return filter === 'all' || isPermissionActive(permission) === (filter === 'active')
}

export type PermissionRisk = 'standard' | 'sensitive' | 'critical'

export const AGENT_STUDIO_ACCESS = 'agent-studio-view'
export const PLATFORM_REVIEW_ACCESS = 'platform-review-view'

export const CATEGORY_LABELS: Record<string, string> = {
  'agent-studio': 'Agent Studio',
  'platform-review': 'Platform Review',
  unilever: 'Unilever',
  cashback: 'Sistema de Cashback',
  logs: 'Sistema de Logs',
  ai: 'Inteligência Artificial',
  users: 'Controle de Usuários',
  nps: 'Gerenciador de Enquetes',
  campaign: 'Simulador de Campanhas',
  conecta: 'Conecta',
  datatransfer: 'Data Transfer',
  reports: 'Relatórios',
  api: 'API',
  briefing: 'Briefing Promoções',
  'briefing-topicos': 'Briefing Promoções - Tópicos',
  seller: 'Seller Mapping',
  infrapay: 'Infrapay',
  devops: 'DevOps',
  settings: 'Configurações',
  fechamentos: 'Fechamentos',
}

const AGENT_STUDIO_MODULES = [
  {
    key: 'access',
    label: 'Acesso geral',
    description: 'Libera a entrada no módulo e serve de base para todas as demais permissões.',
  },
  {key: 'dashboard', label: 'Dashboard', description: 'Visão inicial e indicadores operacionais.'},
  {
    key: 'activities',
    label: 'Minhas Atividades',
    description: 'Pendências, aprovações e pedidos de saldo.',
  },
  {
    key: 'agents',
    label: 'Agentes',
    description: 'Consulta, configuração, chat e exclusão de agentes.',
  },
  {key: 'flows', label: 'Fluxos', description: 'Consulta, edição, execução e exclusão de fluxos.'},
  {
    key: 'knowledge-bases',
    label: 'Bases de Conhecimento',
    description: 'Bases, documentos e conteúdo usado pelos agentes.',
  },
  {key: 'runs', label: 'Execuções', description: 'Histórico e detalhes das execuções.'},
  {
    key: 'budget',
    label: 'Áreas & Budget',
    description: 'Áreas, pessoas, limites, modelos e solicitações de saldo.',
  },
  {key: 'costs', label: 'Custos', description: 'Custos consolidados e dados de auditoria.'},
  {
    key: 'users',
    label: 'Usuários',
    description: 'Administração de usuários vinculados ao Agent Studio.',
  },
] as const

export type AgentStudioModule = typeof AGENT_STUDIO_MODULES[number]

const PLATFORM_REVIEW_MODULES = [
  {key: 'access', label: 'Acesso geral', description: 'Libera a entrada no Platform Review.'},
  {key: 'dashboard', label: 'Dashboard', description: 'Indicadores e visão geral da plataforma.'},
  {key: 'feedbacks', label: 'Feedbacks', description: 'Consulta de avaliações e atendimentos.'},
  {
    key: 'opportunities',
    label: 'Oportunidades',
    description: 'Consulta e gestão de oportunidades.',
  },
  {key: 'customers', label: 'Clientes', description: 'Consulta dos clientes e seu histórico.'},
  {key: 'alerts', label: 'Alertas', description: 'Consulta e gestão dos alertas.'},
  {key: 'reports', label: 'Relatórios', description: 'Consulta e geração de relatórios.'},
  {
    key: 'assistant',
    label: 'Assistente',
    description: 'Uso do assistente de inteligência artificial.',
  },
  {key: 'connections', label: 'Conexões', description: 'Consulta e configuração das integrações.'},
  {
    key: 'settings',
    label: 'Configurações',
    description: 'Administração das configurações da plataforma.',
  },
  {
    key: 'sync',
    label: 'Sincronização',
    description: 'Execução da sincronização das fontes de dados.',
  },
  {
    key: 'ai',
    label: 'Processamento de IA',
    description: 'Execução das análises de inteligência artificial.',
  },
] as const

export type PermissionModule = {key: string; label: string; description: string}

const CRITICAL_PERMISSIONS = new Set([
  'agent-studio-activities-decide',
  'agent-studio-agents-delete',
  'agent-studio-flows-delete',
  'agent-studio-knowledge-bases-delete',
  'agent-studio-budget-manage',
  'agent-studio-users-manage',
  'platform-review-connections-manage',
  'platform-review-settings-manage',
])

const SENSITIVE_PERMISSIONS = new Set([
  'agent-studio-agents-manage',
  'agent-studio-agents-chat',
  'agent-studio-flows-manage',
  'agent-studio-flows-execute',
  'agent-studio-knowledge-bases-manage',
  'agent-studio-budget-request',
  'agent-studio-costs-view',
  'platform-review-opportunities-manage',
  'platform-review-alerts-manage',
  'platform-review-reports-manage',
  'platform-review-assistant-use',
  'platform-review-sync-execute',
  'platform-review-ai-execute',
])

const DEPENDENCIES: Record<string, string[]> = {
  'agent-studio-activities-decide': ['agent-studio-activities-view'],
  'agent-studio-agents-manage': ['agent-studio-agents-view'],
  'agent-studio-agents-delete': ['agent-studio-agents-view'],
  'agent-studio-agents-chat': ['agent-studio-agents-view'],
  'agent-studio-flows-manage': ['agent-studio-flows-view'],
  'agent-studio-flows-delete': ['agent-studio-flows-view'],
  'agent-studio-flows-execute': ['agent-studio-flows-view', 'agent-studio-flows-manage'],
  'agent-studio-knowledge-bases-manage': ['agent-studio-knowledge-bases-view'],
  'agent-studio-knowledge-bases-delete': ['agent-studio-knowledge-bases-view'],
  'agent-studio-budget-manage': ['agent-studio-budget-view'],
  'platform-review-opportunities-manage': ['platform-review-opportunities-view'],
  'platform-review-alerts-manage': ['platform-review-alerts-view'],
  'platform-review-reports-manage': ['platform-review-reports-view'],
  'platform-review-connections-manage': ['platform-review-connections-view'],
  'platform-review-settings-manage': ['platform-review-opportunities-view'],
  'platform-review-sync-execute': ['platform-review-connections-view'],
  'platform-review-ai-execute': ['platform-review-opportunities-view'],
}

export function isAgentStudioPermission(permissionOrName: Permission | string) {
  const name = typeof permissionOrName === 'string' ? permissionOrName : permissionOrName.name
  return name === AGENT_STUDIO_ACCESS || name.startsWith('agent-studio-')
}

export function isPlatformReviewPermission(permissionOrName: Permission | string) {
  const name = typeof permissionOrName === 'string' ? permissionOrName : permissionOrName.name
  return name.startsWith('platform-review-')
}

export function isModulePermission(permissionOrName: Permission | string) {
  return isAgentStudioPermission(permissionOrName) || isPlatformReviewPermission(permissionOrName)
}

export function getPermissionCategory(name: string) {
  if (isAgentStudioPermission(name)) return 'agent-studio'
  if (isPlatformReviewPermission(name)) return 'platform-review'
  if (name.startsWith('briefing-promocoes-topicos-')) return 'briefing-topicos'
  return name.split('-')[0] || 'outros'
}

export function getCategoryLabel(category: string) {
  return CATEGORY_LABELS[category] || category
}

export function getAgentStudioModule(name: string): AgentStudioModule {
  if (name === AGENT_STUDIO_ACCESS) return AGENT_STUDIO_MODULES[0]

  const suffix = name.replace(/^agent-studio-/, '')
  const module = AGENT_STUDIO_MODULES.find(
    (item) => item.key !== 'access' && (suffix === item.key || suffix.startsWith(`${item.key}-`))
  )

  return module || AGENT_STUDIO_MODULES[0]
}

export function getAgentStudioModuleOrder(name: string) {
  const module = getAgentStudioModule(name)
  return AGENT_STUDIO_MODULES.findIndex((item) => item.key === module.key)
}

export function getPermissionModule(name: string): PermissionModule {
  if (!isPlatformReviewPermission(name)) return getAgentStudioModule(name)
  const suffix = name.replace(/^platform-review-/, '')
  return (
    PLATFORM_REVIEW_MODULES.find(
      (item) => item.key !== 'access' && (suffix === item.key || suffix.startsWith(`${item.key}-`))
    ) || PLATFORM_REVIEW_MODULES[0]
  )
}

export function getPermissionModuleOrder(name: string) {
  if (!isPlatformReviewPermission(name)) return getAgentStudioModuleOrder(name)
  return PLATFORM_REVIEW_MODULES.findIndex((item) => item.key === getPermissionModule(name).key)
}

export function getPermissionRisk(name: string): PermissionRisk {
  if (CRITICAL_PERMISSIONS.has(name)) return 'critical'
  if (SENSITIVE_PERMISSIONS.has(name)) return 'sensitive'
  return 'standard'
}

export function getPermissionDependencies(name: string) {
  if (isPlatformReviewPermission(name)) {
    return name === PLATFORM_REVIEW_ACCESS
      ? []
      : [PLATFORM_REVIEW_ACCESS, ...(DEPENDENCIES[name] || [])]
  }
  if (!isAgentStudioPermission(name) || name === AGENT_STUDIO_ACCESS) return []
  return [AGENT_STUDIO_ACCESS, ...(DEPENDENCIES[name] || [])]
}

export function isAgentStudioScreenPermission(name: string) {
  return name === AGENT_STUDIO_ACCESS || name.endsWith('-view')
}

export function isModuleScreenPermission(name: string) {
  return isModulePermission(name) && name.endsWith('-view')
}

export function changePermissionSelection(
  permissions: Permission[],
  current: PermissionId[],
  targets: Permission[],
  checked: boolean
) {
  const next = new Set(current)
  const byName = new Map(permissions.map((permission) => [permission.name, permission]))

  const addPermission = (permission: Permission) => {
    next.add(permission.id_feature)
    getPermissionDependencies(permission.name).forEach((name) => {
      const dependency = byName.get(name)
      if (dependency) addPermission(dependency)
    })
  }

  const removePermission = (permission: Permission) => {
    next.delete(permission.id_feature)
    permissions.forEach((candidate) => {
      if (
        next.has(candidate.id_feature) &&
        getPermissionDependencies(candidate.name).includes(permission.name)
      ) {
        removePermission(candidate)
      }
    })
  }

  targets.forEach((permission) =>
    checked ? addPermission(permission) : removePermission(permission)
  )
  return Array.from(next)
}
